import { useId, useRef, useState, type DragEvent } from 'react';
import { AlertCircle, Upload } from 'lucide-react';
import { AttachmentTile } from '@/components/ui/AttachmentTile';
import { cn } from '@/lib/utils';

export interface FileUploadProps {
  label: string;
  /**
   * The chosen file, owned by the caller — this component holds no state of its
   * own. A name and size is enough, so a file already stored (an edit's current
   * attachment) shows the same way, with the same remove button. Size is null
   * when the stored file's is not known.
   */
  file: { name: string; size: number | null } | null;
  /** Called with the file, or `null` when it is taken back off. */
  onSelect: (file: File | null) => void;
  /**
   * Where a stored file (an edit's current attachment, passed as a name and
   * size) can be fetched, so its tile opens a preview. A file chosen just now
   * previews from memory and needs none.
   */
  previewPath?: string;
  /**
   * An edit's stored file: shown as a tile that previews, above the picker,
   * until a new file is chosen — which replaces it on save.
   */
  current?: { name: string; size: number | null; path: string } | null;
  /**
   * The X on `current`'s tile. The form records the removal and sends it with
   * the save; absent, the stored file can only be replaced.
   */
  onRemoveCurrent?: () => void;
  hint?: string;
  error?: string;
  /** Comma-separated MIME types, matching what the server will accept. */
  accept?: string;
  /** Megabytes. Must match the server's limit or the user finds out late. */
  maxSizeMb?: number;
  disabled?: boolean;
  /** Marks the slot with the same asterisk as every other required field. */
  required?: boolean;
}

/** A scan or a photo of a certificate — what an institute office actually has. */
const ACCEPTED = 'application/pdf,image/png,image/jpeg';

const TYPE_LABEL: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
};

function describeAccepted(accept: string): string {
  const names = accept.split(',').map((type) => TYPE_LABEL[type.trim()] ?? type.trim());
  return names.join(', ');
}

/**
 * Attaching one document — a birth certificate, a transfer certificate, an ID proof.
 */
export function FileUpload({
  label,
  file,
  onSelect,
  previewPath,
  current = null,
  onRemoveCurrent,
  hint,
  error,
  accept = ACCEPTED,
  required = false,
  maxSizeMb = 5,
  disabled = false,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldId = useId();
  const messageId = `${fieldId}-message`;
  const [localError, setLocalError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const allowed = accept.split(',').map((type) => type.trim());

  function handleFile(chosen: File | undefined) {
    if (!chosen) return;

    if (!allowed.includes(chosen.type)) {
      setLocalError(`Attach a ${describeAccepted(accept)} file`);
      return;
    }
    if (chosen.size > maxSizeMb * 1024 * 1024) {
      setLocalError(`That file is larger than ${maxSizeMb} MB`);
      return;
    }

    setLocalError(null);
    onSelect(chosen);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    handleFile(event.dataTransfer.files?.[0]);
  }

  function clear() {
    setLocalError(null);
    onSelect(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  const message = error ?? localError;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
        {label}
        {required && <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>}
      </label>

      {/* The stored file a new one would replace. */}
      {!file && current && (
        <AttachmentTile
          name={current.name}
          size={current.size}
          path={current.path}
          onRemove={onRemoveCurrent}
          disabled={disabled}
        />
      )}

      {file ? (
        <AttachmentTile
          name={file.name}
          size={file.size}
          file={file instanceof File ? file : null}
          path={file instanceof File ? undefined : previewPath}
          onRemove={clear}
          disabled={disabled}
        />
      ) : (
        <div
          onDragOver={(event) => {
            event.preventDefault();
            if (!disabled) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <button
            type="button"
            id={fieldId}
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            aria-describedby={message || hint ? messageId : undefined}
            className={cn(
              'group relative flex items-center gap-3 rounded-xl border-2 border-dashed px-3.5 py-2.5 text-left transition-all duration-200 cursor-pointer w-full',
              'focus:outline-none focus:ring-2 focus:ring-indigo-500/20',
              'disabled:cursor-not-allowed disabled:opacity-50',
              isDragging
                ? 'border-indigo-500 bg-indigo-50/80 dark:bg-indigo-950/40 scale-[1.01] shadow-md'
                : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-indigo-400 dark:hover:border-indigo-600 hover:bg-indigo-50/30 dark:hover:bg-indigo-950/20 hover:shadow-xs',
              message && 'border-rose-400 dark:border-rose-800 bg-rose-50/30 dark:bg-rose-950/20',
            )}
          >
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-900/50 text-indigo-600 dark:text-indigo-400 group-hover:scale-105 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-200 shadow-2xs">
              <Upload className="h-4 w-4" strokeWidth={2.2} />
            </div>

            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-slate-800 dark:text-slate-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                  Choose file
                </span>
                <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500">
                  or drag here
                </span>
              </div>
              <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500 truncate">
                {describeAccepted(accept)} up to {maxSizeMb} MB
              </span>
            </div>
          </button>
        </div>
      )}

      {message ? (
        <p id={messageId} role="alert" className="flex items-center gap-1.5 text-xs text-rose-500 dark:text-rose-400 font-medium">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {message}
        </p>
      ) : hint ? (
        <p id={messageId} className="text-xs text-slate-500 dark:text-slate-400 font-medium">
          {hint}
        </p>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        tabIndex={-1}
        disabled={disabled}
        onChange={(event) => handleFile(event.target.files?.[0])}
      />
    </div>
  );
}
